import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import NavMegaPanel from '../overlay/NavMegaPanel';

/** SLS-style nav: click opens full-width mega panel under the header. */
const MENU_ORDER = ['services', 'products', 'casestudies', 'about'];
const THEME_KEY = 'jb-theme';

function getInitialTheme() {
  if (typeof document === 'undefined') return 'light';
  if (document.documentElement.classList.contains('theme-light')) return 'light';
  try {
    const saved = localStorage.getItem(THEME_KEY);
    if (saved === 'dark' || saved === 'light') return saved;
  } catch (_) {}
  return 'light';
}

export default function LayoutNav({
  activeSection,
  content,
  onGetStarted,
  megaMenu,
  onOpenMega,
  onCloseMega,
  caseStudies,
  onNavigateToService,
  onContact,
  workItems,
}) {
  const nav = content?.nav || {};
  const menuItemsRaw = Array.isArray(nav.menuItems) ? nav.menuItems : [];
  const menuItems = MENU_ORDER.map((id) => menuItemsRaw.find((m) => m.id === id)).filter(Boolean);
  const [menuOpen, setMenuOpen] = useState(false);
  const [theme, setTheme] = useState(getInitialTheme);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'light') {
      root.classList.add('theme-light');
      try { localStorage.setItem(THEME_KEY, 'light'); } catch (_) {}
    } else {
      root.classList.remove('theme-light');
      try { localStorage.setItem(THEME_KEY, 'dark'); } catch (_) {}
    }
  }, [theme]);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(THEME_KEY);
      if (saved === 'light' || saved === 'dark') setTheme(saved);
      else setTheme('light');
    } catch (_) {
      setTheme('light');
    }
  }, []);

  useEffect(() => {
    const check = () => (window.innerWidth >= 768 ? setMenuOpen(false) : null);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    document.body.style.overflow = megaMenu ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [megaMenu]);

  const setDark = () => { setTheme('dark'); setMenuOpen(false); onCloseMega && onCloseMega(); };
  const setLight = () => { setTheme('light'); setMenuOpen(false); onCloseMega && onCloseMega(); };

  const handleClick = (id) => {
    if (megaMenu === id) onCloseMega && onCloseMega();
    else onOpenMega && onOpenMega(id);
    setMenuOpen(false);
  };

  const handleGetStarted = () => {
    setMenuOpen(false);
    onCloseMega && onCloseMega();
    if (onGetStarted) onGetStarted();
  };

  return (
    <header
      className="fixed top-0 left-0 right-0 z-[200] bg-forest-bg/95 backdrop-blur-xl border-b"
      style={{ borderColor: 'var(--jb-border)' }}
    >
      <div className="flex items-center justify-between px-4 sm:px-6 md:px-8 lg:px-12 py-4">
        <div className="font-display font-extrabold text-lg sm:text-xl text-text-hero tracking-tight flex items-center gap-2">
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); setDark(); window.location.hash = '#'; }}
            className={`uppercase hover:text-cyan transition-colors ${theme === 'dark' ? 'text-cyan' : ''}`}
            aria-label="Jungle – dark theme"
          >
            Jungle
          </button>
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); setLight(); window.location.hash = '#'; }}
            className={`uppercase hover:text-cyan transition-colors ${theme === 'light' ? 'text-cyan' : ''}`}
            aria-label="Brick – light theme"
          >
            Brick
          </button>
        </div>

        <nav className="hidden md:flex items-center gap-1 lg:gap-2">
          {menuItems.map((item) => {
            const open = megaMenu === item.id;
            return (
              <button
                key={item.id}
                data-mega-trigger
                onClick={() => handleClick(item.id)}
                aria-expanded={open}
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  open || activeSection === item.id
                    ? 'text-cyan bg-cyan/10'
                    : 'text-text-sub hover:text-cyan hover:bg-white/5'
                }`}
              >
                {item.label ?? ''}
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
              </button>
            );
          })}
          <button
            type="button"
            onClick={handleGetStarted}
            className="jb-cta ml-3 px-5 py-2.5 rounded-lg bg-cyan text-forest-bg font-body font-bold text-sm tracking-wide hover:opacity-90 transition"
          >
            Get Started
          </button>
        </nav>

        <div className="flex md:hidden items-center gap-2">
          <button
            type="button"
            onClick={() => setMenuOpen(!menuOpen)}
            className="p-2 text-text-sub hover:text-cyan"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          >
            {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden px-4 pb-4 flex flex-col gap-1 border-t" style={{ borderColor: 'var(--jb-border)' }}>
          {menuItems.map((item) => (
            <button
              key={item.id}
              data-mega-trigger
              onClick={() => handleClick(item.id)}
              className={`text-left px-4 py-3 rounded-lg text-sm font-medium inline-flex items-center justify-between ${
                megaMenu === item.id || activeSection === item.id ? 'text-cyan bg-forest-card' : 'text-text-body'
              }`}
            >
              {item.label ?? ''}
              <ChevronDown className={`w-4 h-4 opacity-60 ${megaMenu === item.id ? 'rotate-180' : ''}`} />
            </button>
          ))}
          <button
            type="button"
            onClick={handleGetStarted}
            className="jb-cta mt-2 px-5 py-3 rounded-lg bg-cyan text-forest-bg font-bold text-sm text-center"
          >
            Get Started
          </button>
        </div>
      )}

      {megaMenu && (
        <NavMegaPanel
          type={megaMenu}
          content={content}
          caseStudies={caseStudies}
          workItems={workItems}
          onClose={onCloseMega}
          onNavigateToService={onNavigateToService}
          onContact={onContact}
          onOpenMega={onOpenMega}
        />
      )}
    </header>
  );
}
